import React, { useState } from 'react';
import axios from 'axios';
import { css } from '@emotion/react';
import { AppButton, AppPasswordField, AppTextField } from '../../../../Components/FormKit';
import { sourceTrimRoute, url } from '../../../../env';
import { clearSourceTrimAccess } from '../../../../Utils/sourceTrimAccess';

function SourceTrimAccessCode({ toast }) {
    const [newCode, setNewCode] = useState('');
    const [confirmCode, setConfirmCode] = useState('');
    const [loading, setLoading] = useState(false);

    const canSubmit = newCode.trim().length >= 6 && newCode === confirmCode && !loading;

    const notify = (severity, summary, detail) => {
        toast?.current?.show({ severity, summary, detail, life: 4000 });
    };

    const shareUrl = `${window.location.origin}${sourceTrimRoute}`;

    const copyShareUrl = async () => {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(shareUrl);
            } else {
                const input = document.createElement('textarea');
                input.value = shareUrl;
                input.setAttribute('readonly', '');
                input.style.position = 'absolute';
                input.style.left = '-9999px';
                document.body.appendChild(input);
                input.select();
                document.execCommand('copy');
                document.body.removeChild(input);
            }
            notify('success', 'Ruta copiada', 'Ya puedes compartirla.');
        } catch {
            notify('error', 'No se pudo copiar', 'Copia la ruta manualmente.');
        }
    };

    const submit = async (event) => {
        event.preventDefault();

        if (newCode.trim().length < 6) {
            notify('warn', 'Clave corta', 'Usa al menos 6 caracteres.');
            return;
        }

        if (newCode !== confirmCode) {
            notify('warn', 'No coincide', 'Confirma la misma clave.');
            return;
        }

        setLoading(true);
        try {
            await axios.put(
                `${url}/cotizador3/access-code`,
                { new_code: newCode.trim() },
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
            clearSourceTrimAccess();
            setNewCode('');
            setConfirmCode('');
            notify('success', 'Clave actualizada', 'Los accesos anonimos anteriores quedaron invalidados.');
        } catch (e) {
            notify('error', 'Error', e?.response?.data?.message ?? 'No se pudo actualizar la clave.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <section css={sourceTrimAccessCodeCSS()} className="fk-root">
            <form className="stac-card" onSubmit={submit}>
                <div className="stac-header">
                    <span>Acceso oculto</span>
                    <h2>Clave cotizador3</h2>
                    <p>Cambia la clave anonima de la ruta oculta.</p>
                </div>

                <div className="stac-info">
                    <strong>Que pasa al guardar</strong>
                    <p>Al guardar, todos los accesos anonimos anteriores dejaran de funcionar.</p>
                    <ul>
                        <li>Los usuarios internos siguen entrando sin clave.</li>
                        <li>Los anonimos tendran que ingresar la nueva clave.</li>
                        <li>Usa minimo 6 caracteres.</li>
                    </ul>
                </div>

                <div className="stac-share">
                    <AppTextField
                        label="Ruta para compartir"
                        value={shareUrl}
                        readOnly
                        onFocus={(event) => event.target.select()}
                    />
                    <AppButton
                        type="button"
                        label="Copiar ruta"
                        icon="pi pi-copy"
                        variant="info"
                        onClick={copyShareUrl}
                    />
                </div>

                <AppPasswordField
                    label="Nueva clave"
                    value={newCode}
                    onChange={(event) => setNewCode(event.target.value)}
                    autoComplete="new-password"
                    invalid={newCode.length > 0 && newCode.trim().length < 6}
                    errorMessage={newCode.length > 0 && newCode.trim().length < 6 ? 'Usa al menos 6 caracteres.' : ''}
                />

                <AppPasswordField
                    label="Confirmar clave"
                    value={confirmCode}
                    onChange={(event) => setConfirmCode(event.target.value)}
                    autoComplete="new-password"
                    invalid={confirmCode.length > 0 && newCode !== confirmCode}
                    errorMessage={confirmCode.length > 0 && newCode !== confirmCode ? 'Las claves no coinciden.' : ''}
                />

                <AppButton
                    type="submit"
                    label={loading ? 'Guardando...' : 'Guardar clave'}
                    icon="pi pi-save"
                    loading={loading}
                    disabled={!canSubmit}
                    fullWidth
                />
            </form>
        </section>
    );
}

function sourceTrimAccessCodeCSS() {
    return css`
        min-height: 56vh;
        padding: 32px 16px;
        display: flex;
        align-items: center;
        justify-content: center;

        .stac-card {
            width: min(100%, 620px);
            display: flex;
            flex-direction: column;
            gap: 16px;
            font-family: var(--fk-font);
            padding: 28px;
            border-radius: 28px;
            background: rgba(255, 255, 255, 0.86);
            box-shadow: 2px 2px 11px rgba(33, 88, 140, 0.08);
            backdrop-filter: blur(5px);
            animation: stacCardIn 0.28s ease-out;
        }

        .stac-header {
            text-align: center;
            display: flex;
            flex-direction: column;
            gap: 6px;
        }

        .stac-header span {
            color: #22a37e;
            font-size: 0.75rem;
            letter-spacing: 0.14em;
            font-weight: 700;
            text-transform: uppercase;
        }

        h2 {
            margin: 0;
            font-size: 1.35rem;
            color: #21588C;
            letter-spacing: 0.02em;
        }

        .stac-header p {
            margin: 0;
            color: #587183;
            font-size: 0.85rem;
            line-height: 1.45;
        }

        .stac-info {
            padding: 14px 16px;
            border: 1px solid #d8eef8;
            border-radius: 18px;
            background: #f5fcfe;
            color: #475569;
            font-size: 0.82rem;
            line-height: 1.45;
        }

        .stac-info strong {
            display: block;
            color: #21588C;
            font-size: 0.86rem;
            margin-bottom: 4px;
        }

        .stac-info p {
            margin: 0 0 8px;
        }

        .stac-info ul {
            margin: 0;
            padding-left: 18px;
        }

        .stac-info li + li {
            margin-top: 4px;
        }

        .stac-share {
            display: grid;
            grid-template-columns: minmax(0, 1fr) auto;
            gap: 12px;
            align-items: end;
        }

        @media (max-width: 650px) {
            .stac-share {
                grid-template-columns: 1fr;
            }
        }

        @keyframes stacCardIn {
            from {
                opacity: 0;
                transform: translateY(10px) scale(0.98);
            }
            to {
                opacity: 1;
                transform: translateY(0) scale(1);
            }
        }
    `;
}

export default SourceTrimAccessCode;
